// Компонент упражнения на лексику (lexics): текст с пропусками и банк слов для каждого пропуска.
// Студент кликает по слову из банка, чтобы вставить его в соответствующий пропуск.

import { useState } from 'react';
import { Button, Modal, Row, Col } from 'react-bootstrap';

// ── Компонент пропуска ────────────────────────────────────────────────────────
function BlankSlot({ pos, filledWord, onClear }) {
  if (filledWord) {
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.25rem',
          background: '#4f6ef7',
          color: '#fff',
          borderRadius: '0.375rem',
          padding: '2px 10px',
          fontWeight: 600,
          fontSize: '0.9rem',
          cursor: 'default',
          verticalAlign: 'middle',
          margin: '0 2px',
          lineHeight: 1.5,
        }}
      >
        {filledWord}
        <span
          onClick={() => onClear(pos)}
          title="Убрать слово"
          style={{ fontSize: '0.7rem', opacity: 0.8, lineHeight: 1, cursor: 'pointer' }}
        >
          ×
        </span>
      </span>
    );
  }
  return (
    <span
      style={{
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'center',
        verticalAlign: 'middle',
        margin: '0 2px',
        gap: 0,
      }}
    >
      <span
        style={{
          fontSize: '0.65rem',
          color: '#0d6efd',
          lineHeight: 1,
          fontWeight: 700,
          letterSpacing: '0.02em',
          userSelect: 'none',
        }}
      >
        {pos}
      </span>
      <span
        style={{
          display: 'inline-block',
          minWidth: '80px',
          height: '1.5em',
          borderRadius: '0.375rem',
          border: '2px dashed #93b0fb',
          background: 'transparent',
        }}
      />
    </span>
  );
}

/**
 * LexicsTextItem — блок упражнения на лексику (текст с пропусками + банк слов).
 *
 * Props:
 *   tokens:        Array<{type:'text',value:string} | {type:'blank',pos:string}>
 *   wordsBank:     { [blankPos]: string[] }
 *   answers:       { [blankPos]: string }   — blankPos → выбранное слово
 *   onWordClick:   (blankPos, word) => void
 *   onClearBlank:  (blankPos) => void
 *   explanation:   { title: string, description: string } | null
 */
export default function LexicsTextItem({ tokens = [], wordsBank = {}, answers = {}, onWordClick, onClearBlank, explanation }) {
  const [showModal, setShowModal] = useState(false);

  const blankPositions = Object.keys(wordsBank).sort((a, b) => Number(a) - Number(b));

  return (
    <>
    <Row className="g-4">

      {/* ── Левая колонка: текст с пропусками ── */}
      <Col xs={12} md={7}>
        <div style={{ position: 'relative' }}>
          {explanation && (
            <button
              type="button"
              onClick={() => setShowModal(true)}
              title="Показать правило"
              style={{
                position: 'absolute',
                top: 0,
                right: 0,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '1.5rem',
                height: '1.5rem',
                borderRadius: '50%',
                border: '1.5px solid #6c757d',
                background: 'transparent',
                color: '#6c757d',
                fontSize: '0.75rem',
                fontWeight: 700,
                cursor: 'pointer',
                padding: 0,
                lineHeight: 1,
                transition: 'border-color 0.15s, color 0.15s',
                zIndex: 1,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#0dcaf0';
                e.currentTarget.style.color = '#0dcaf0';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#6c757d';
                e.currentTarget.style.color = '#6c757d';
              }}
            >
              ?
            </button>
          )}

          {tokens.length > 0 ? (
            <p
              style={{
                fontSize: '1rem',
                lineHeight: 2.1,
                color: '#1e2a4a',
                margin: 0,
                wordBreak: 'break-word',
                paddingRight: explanation ? '2rem' : 0,
              }}
            >
              {tokens.map((tok, idx) => {
                if (tok.type === 'text') return <span key={idx}>{tok.value}</span>;
                return (
                  <BlankSlot
                    key={idx}
                    pos={tok.pos}
                    filledWord={answers[tok.pos] ?? null}
                    onClear={onClearBlank}
                  />
                );
              })}
            </p>
          ) : (
            <div className="text-center py-4 text-muted" style={{ fontSize: '0.9rem' }}>
              Нет данных для отображения
            </div>
          )}
        </div>
      </Col>

      {/* ── Правая колонка: банк слов (sticky) ── */}
      {blankPositions.length > 0 && (
        <Col xs={12} md={5}>
          <div style={{ position: 'sticky', top: '1rem' }}>
            <div
              style={{
                fontSize: '0.75rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: '#6c757d',
                marginBottom: '0.875rem',
              }}
            >
              Банк слов
            </div>
            <div className="d-flex flex-column gap-3">
              {blankPositions.map((bp) => {
                const words    = wordsBank[bp] ?? [];
                const selected = answers[bp] ?? null;
                return (
                  <div key={bp}>
                    <div
                      style={{
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        color: '#4f6ef7',
                        marginBottom: '0.5rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                      }}
                    >
                      Пропуск {bp}
                    </div>
                    <div className="d-flex flex-wrap gap-2">
                      {words.map((word) => {
                        const isSelected = selected === word;
                        return (
                          <button
                            key={word}
                            type="button"
                            onClick={() => onWordClick(bp, word)}
                            style={{
                              background: isSelected ? '#4f6ef7' : '#fff',
                              color: isSelected ? '#fff' : '#1e2a4a',
                              border: `2px solid ${isSelected ? '#4f6ef7' : '#dee2e6'}`,
                              borderRadius: '2rem',
                              padding: '5px 16px',
                              cursor: 'pointer',
                              fontWeight: 600,
                              fontSize: '0.875rem',
                              fontFamily: 'inherit',
                              lineHeight: 1.4,
                              transition: 'border-color 0.15s, color 0.15s, background 0.15s',
                              outline: 'none',
                            }}
                            onMouseEnter={(e) => {
                              if (!isSelected) {
                                e.currentTarget.style.borderColor = '#4f6ef7';
                                e.currentTarget.style.color = '#4f6ef7';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isSelected) {
                                e.currentTarget.style.borderColor = '#dee2e6';
                                e.currentTarget.style.color = '#1e2a4a';
                              }
                            }}
                          >
                            {word}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </Col>
      )}

    </Row>

    {/* Модальное окно с explanation */}
    {explanation && (
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton style={{ borderBottom: '1px solid rgba(0,0,0,0.06)', padding: '1.25rem 1.5rem' }}>
          <Modal.Title style={{ fontSize: '1rem', fontWeight: 700 }}>
            {explanation.title ?? 'Правило'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '1.5rem' }}>
          <p style={{ lineHeight: 1.7, color: '#1e2a4a', whiteSpace: 'pre-wrap', margin: 0 }}>
            {explanation.description}
          </p>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: '1px solid rgba(0,0,0,0.06)', padding: '1rem 1.5rem' }}>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => setShowModal(false)}
            style={{ borderRadius: '0.5rem', fontWeight: 500 }}
          >
            Закрыть
          </Button>
        </Modal.Footer>
      </Modal>
    )}
    </>
  );
}
